import Bugsnag from "@bugsnag/expo";
import { loadAsync } from "expo-font";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { APP_ENV } from "./src/client";
import App from "./App";
import FakeSplashScreen from "./src/components/FakeSplashScreen/FakeSplashScreen";

import * as SplashScreen from "expo-splash-screen";
import { useUpdateOnFocus } from "./src/hooks/useUpdateOnFocus";
import { cmsDataAtom } from "./src/atoms";
import { useSetAtom } from "jotai/react";

import { getCmsData } from "./src/client/cms";
import useRefreshUser from "./src/hooks/useRefreshUser";

// Prevent native splash screen from autohiding before App component declaration
if (Platform.OS != "web") {
  SplashScreen.preventAutoHideAsync()
    .then((result) => {
      console.log(`SplashScreen.preventAutoHideAsync() succeeded: ${result}`);

      Bugsnag.leaveBreadcrumb("SplashScreen.preventAutoHideAsync() succeeded");
    })
    .catch(console.warn);
}

// might need to put this in a shared file as this file
// is only for web really
let skipBugsnag = false;
if (Platform.OS === "web") {
  // initialize dataLayer
  window.dataLayer = window?.dataLayer || [];
  if (window.location.href.includes("localhost")) {
    skipBugsnag = true;
  }
}

if (!skipBugsnag && !Bugsnag.isStarted()) {
  Bugsnag.start({
    releaseStage: APP_ENV,
  });
}

const loadFontsAsync = async () => {
  try {
    const fontsModule = await import("./src/styles/fonts");
    await loadAsync(fontsModule.FONTS);
  } catch (err) {
    console.error("Error loading fonts.... ", err);
    Bugsnag.notify(
      new Error(`[Setup] Error loading fonts during startup: ${err}`)
    );
  }
};

const RootElement = () => {
  const [ready, setReady] = useState(false);
  const setCmsData = useSetAtom(cmsDataAtom);
  const { refreshUser } = useRefreshUser();

  // Fake splash screen logic - only runs on apps
  const [showFakeSplashScreen, setShowFakeSplashScreen] = useState(false);

  // useUpdateOnFocus will check for updates and apply
  // them if available - updating our setter
  // which will show the fake splash screen below
  useUpdateOnFocus({ setShowFakeSplashScreen });

  useEffect(() => {
    const init = async () => {
      // If the app is not ready, we can show a splash screen
      if (!ready) {
        console.log("[Setup] Initializing App...");
        Bugsnag.leaveBreadcrumb("App initialization started");

        // Hide the native splash screen so the FakeSplashScreen can take over
        await SplashScreen.hideAsync();
        Bugsnag.leaveBreadcrumb("[Setup] Native Splash Screen hidden");
        console.log("[Setup] Native Splash Screen hidden");

        // init bugsnag nomatter what
        if (!skipBugsnag && !Bugsnag.isStarted()) {
          Bugsnag.start({
            releaseStage: APP_ENV,
          });
        }

        // sleep 5 seconds to simulate loading
        console.log("[Setup] started sleeping for 5 seconds...");
        await new Promise((resolve) => setTimeout(resolve, 5000));
        console.log("[Setup] finished sleeping");

        await loadFontsAsync()
          .then(async () => {
            console.log("[Setup] Fonts Loaded");
            Bugsnag.leaveBreadcrumb("[Setup] Fonts Loaded");

            await getCmsData()
              .then((cmsData) => {
                setCmsData(cmsData);
                console.log("[Setup] Fetched Settings");
              })
              .catch((err) => {
                console.error("[Setup] Error fetching CMS data: ", err);
                Bugsnag.notify(
                  new Error(`[Setup] Error fetching CMS data: ${err}`)
                );
              });

            // refresh user data
            await refreshUser()
              .then(() => {
                console.log("[Setup] User data refreshed");
                Bugsnag.leaveBreadcrumb("[Setup] User data refreshed");
              })
              .catch((err) => {
                console.error("[Setup] Error refreshing user data: ", err);
                Bugsnag.notify(
                  new Error(`[Setup] Error refreshing user data: ${err}`)
                );
              });

            // Other stuff..
            console.log("[Setup] App Initialized");
            Bugsnag.leaveBreadcrumb("[Setup] Fonts Loaded");
            setReady(true);
          })
          .catch((err) => {
            console.error("[Setup] Error during app startup: ", err);
            Bugsnag.notify(
              new Error(`[Setup] Error during app startup: ${err}`)
            );
            setReady(true); // set ready to true even if fonts fail to load as its better than a blank screen
          });
      }
    };

    init();
  }, []);

  if (!ready || showFakeSplashScreen) {
    return <FakeSplashScreen />;
  }

  return <App />;
};

export default RootElement;
